import type { App } from "vue"
import PrimeVue from "primevue/config"
import { definePreset } from "@primevue/themes"
import Lara from "@primevue/themes/lara"

// Services
import ConfirmationService from "primevue/confirmationservice"
import ToastService from "primevue/toastservice"

const MyPresent = definePreset(Lara, {
    primitive: {
        blue: {
            10: "#f5f7fb",
            50: "#edf4fe",
            100: "#d8e9fd",
            200: "#b6d3fb",
            300: "#81ACEE",
            400: "#6586D5",
            500: "#3E55B4",
            600: "#2A3988",
            700: "#151C5D",
            800: "#2a365e",
            900: "#000032",
            950: "#000019",
        },
        cyan: {
            50: "#f3fbfd",
            100: "#c3edf5",
            200: "#94e0ed",
            300: "#65d2e4",
            400: "#35c4dc",
            500: "#06b6d4",
            600: "#059bb4",
            700: "#047f94",
            800: "#036475",
            900: "#024955",
        },
        orange: {
            50: "#FFF8F3",
            100: "#feddc7",
            200: "#fcc39b",
            300: "#fba86f",
            400: "#fa8e42",
            500: "#F97316",
            600: "#d46213",
            700: "#ae510f",
            800: "#893f0c",
            900: "#642e09",
        },
        green: {
            50: "#f4fcf7",
            100: "#caf1d8",
            200: "#a0e6ba",
            300: "#76db9b",
            400: "#4cd07d",
            500: "#22c55e",
            600: "#1da750",
            700: "#188a42",
            800: "#136c34",
            900: "#0e4f26",
        },
        bluegray: {
            50: "#f7f8f9",
            100: "#dadee3",
            200: "#bcc3cd",
            300: "#9fa9b7",
        },
        slate: {
            50: "#fafafa",
            100: "#f5f5f5",
            200: "#eeeeee",
            300: "#e0e0e0",
            400: "#bdbdbd",
            500: "#9e9e9e",
            600: "#757575",
            700: "#616161",
            800: "#424242",
            900: "#212121",
        },
    },
    semantic: {
        primary: {
            10: "{blue.10}",
            50: "{blue.50}",
            100: "{blue.100}",
            200: "{blue.200}",
            300: "{blue.300}",
            400: "{blue.400}",
            500: "{blue.500}",
            600: "{blue.600}",
            700: "{blue.700}",
            800: "{blue.800}",
            900: "{blue.900}",
            950: "{blue.950}",
        },
        colorScheme: {
            default: "light",
            light: {
                primary: {
                    color: "{blue.700}",
                    contract: "#ffffff",
                    hoverColor: "{blue.800}",
                    activeColor: "{blue.800}",
                },
                highlight: {
                    background: "{blue.100}",
                    focusBackground: "{blue.200}",
                    color: "{blue.600}",
                    focusColor: "{blue.700}",
                },
            },
        },
    },
    components: {
        message: {
            colorScheme: {
                light: {
                    info: {
                        color: "{blue.700}",
                    },
                },
            },
        },
        button: {
            colorScheme: {
                light: {
                    primary: {
                        background: "{primary.800}",
                        color: "#ffffff",
                        borderColor: "{primary.800}",
                        hover: {
                            background: "{primary.700}",
                            color: "#ffffff",
                            borderColor: "{primary.700}",
                        },
                        active: {
                            background: "{primary.600}",
                            color: "#ffffff",
                            borderColor: "{primary.600}",
                        },
                    },
                    secondary: {
                        background: "{cyan.400}",
                        color: "#ffffff",
                        focusShadow: "#e2e8f0",
                        borderColor: "{cyan.400}",
                        hover: {
                            background: "{cyan.500}",
                            color: "#ffffff",
                            borderColor: "{cyan.500}",
                        },
                        active: {
                            background: "{cyan.600}",
                            color: "#ffffff",
                            borderColor: "{cyan.600}",
                        },
                    },
                    outlined: {
                        primary: {
                            border: {
                                color: "{primary.800}",
                            },
                        },
                        secondary: {
                            active: {
                                background: "#64748b29",
                            },
                            hover: {
                                background: "#64748b0a",
                            },
                        },
                    },
                    text: {
                        color: "{primary.700}",
                    },
                    link: {
                        color: "{cyan.500}",
                    },
                },
            },
        },
        togglebutton: {
            checked: {
                background: "{primary.800}",
                borderColor: "{primary.800}",
                color: "#ffffff",
                hover: {
                    background: "{primary.700}",
                    color: "#ffffff",
                    borderColor: "{primary.700}",
                },
                active: {
                    background: "{primary.600}",
                    color: "#ffffff",
                    borderColor: "{primary.600}",
                },
            },
        },
    },
})

export const HomeysTheme = {
    locale: {
        monthNamesShort: [
            "Jan",
            "Fev",
            "Mar",
            "Avr",
            "Mai",
            "Jun",
            "Jul",
            "Aoû",
            "Sep",
            "Oct",
            "Nov",
            "Déc",
        ],
        monthNames: [
            "Janvier",
            "Février",
            "Mars",
            "Avril",
            "Mai",
            "Juin",
            "Juillet",
            "Août",
            "Septembre",
            "Octobre",
            "Novembre",
            "Décembre",
        ],
        dayNamesShort: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
        dayNamesMin: ["Di", "Lu", "Ma", "Me", "Je", "Ve", "Sa"],
        dayNames: [
            "Dimanche",
            "Lundi",
            "Mardi",
            "Mercredi",
            "Jeudi",
            "Vendredi",
            "Samedi",
        ],
        startsWith: "Commence par",
        contains: "Contient",
        notContains: "Ne contient pas",
        endsWith: "Finit par",
        equals: "Égale",
        notEquals: "Différent de",
        noFilter: "Pas de filtre",
        lt: "Inférieur à",
        lte: "Inférieur ou égal à",
        gt: "Supérieur à",
        gte: "Supérieur ou égal à",
        is: "Est",
        isNot: "N'est pas",
        before: "Avant",
        after: "Après",
        // Les messages vides et les boutons communs, jusqu'ici servis en
        // anglais par les valeurs par défaut de `@primevue/core/config`.
        emptyMessage: "Aucune option",
        emptyFilterMessage: "Aucun résultat",
        emptySelectionMessage: "Aucun élément sélectionné",
        clear: "Effacer",
        apply: "Appliquer",
        choose: "Choisir",
        cancel: "Annuler",
        today: "Aujourd'hui",
        weekHeader: "Sem",
        firstDayOfWeek: 1,
        dateFormat: "dd/mm/yy",
        /**
         * CE QU'UNE AIDE TECHNIQUE ENTEND (DEV-1508 lot 4, item 30, E9).
         *
         * Ces textes ne s'écrivent nulle part à l'écran : ils ne vivent que
         * dans les `aria-label` posés par le paginateur, les listes et les
         * tableaux. Sans ce bloc, un lecteur d'écran annonce « Rows per page »,
         * « Next Page » et « Page 3 » sur une surface client francophone.
         *
         * Les noms de clés sont ceux de `@primevue/core/config`, relus un à un
         * dans le worktree : la clé du paginateur est bien `rowsPerPageLabel`,
         * et non `rowsPerPage`.
         */
        aria: {
            pageLabel: "Page {page}",
            firstPageLabel: "Première page",
            lastPageLabel: "Dernière page",
            nextPageLabel: "Page suivante",
            prevPageLabel: "Page précédente",
            rowsPerPageLabel: "Lignes par page",
            jumpToPageDropdownLabel: "Aller à la page",
            jumpToPageInputLabel: "Numéro de page",
            selectRow: "Ligne sélectionnée",
            unselectRow: "Ligne désélectionnée",
            expandRow: "Ligne dépliée",
            collapseRow: "Ligne repliée",
            showFilterMenu: "Afficher les filtres",
            hideFilterMenu: "Masquer les filtres",
            listLabel: "Liste des options",
            close: "Fermer",
            previous: "Précédent",
            next: "Suivant",
            selectAll: "Tout sélectionner",
            unselectAll: "Tout désélectionner",
            trueLabel: "Oui",
            falseLabel: "Non",
            nullLabel: "Non renseigné",
        },
    },
    theme: {
        preset: MyPresent,
        ripple: true,
        options: {
            darkModeSelector: "none",
        },
        cssLayer: {
            name: "primevue",
            order: "theme, base, primevue",
        },
    },
}

export const PRIMVEVUE_SERVICE = [ConfirmationService, ToastService]

/**
 * Permet de charger le plugin PrimeVue avec les composants, directives et services
 * @param app - Application Vue
 */
export function loadPrimeVue(app: App) {
    app.use(PrimeVue, HomeysTheme)

    PRIMVEVUE_SERVICE.forEach((service) => {
        app.use(service)
    })
}
